import type { ReactNode } from 'react';
import classNames from 'classnames';
import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  type ModalProps,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import './AppModal.scss';

export interface AppModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** i18n key shown in the header */
  title: string;
  children: ReactNode;
  onApply?: () => void;
  /** i18n key for the primary button */
  applyTitle?: string;
  applyDisabled?: boolean;
  applyLoading?: boolean;
  onCancel?: () => void;
  /** i18n key for the cancel button */
  cancelTitle?: string;
  hideFooter?: boolean;
  size?: ModalProps['size'];
  motionPreset?: ModalProps['motionPreset'];
  className?: string;
}

export function AppModal({
  isOpen,
  onClose,
  title,
  children,
  onApply,
  applyTitle = 'common.save',
  applyDisabled = false,
  applyLoading = false,
  onCancel,
  cancelTitle = 'cancel',
  hideFooter = false,
  size = 'xl',
  motionPreset,
  className,
}: AppModalProps) {
  const { t } = useTranslation();
  const handleCancel = () => {
    if (onCancel) onCancel();
    else onClose();
  };
  const showFooter = !hideFooter && (Boolean(onApply) || Boolean(onCancel));

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={size}
      isCentered
      motionPreset={motionPreset}
    >
      <ModalOverlay />
      <ModalContent className={classNames('app-modal', className)}>
        <ModalCloseButton className="app-modal__close">
          <i className="icon icon-close-modal" />
        </ModalCloseButton>
        <ModalHeader className="app-modal__header">
          <span className="app-modal__title">{t(title)}</span>
        </ModalHeader>
        <ModalBody className="app-modal__body">{children}</ModalBody>
        {showFooter ? (
          <ModalFooter className="app-modal__footer">
            <Flex gap="10px" justifyContent="center" width="100%">
              <Button
                type="button"
                variant="outline"
                flex="1"
                onClick={handleCancel}
              >
                {t(cancelTitle)}
              </Button>
              {onApply ? (
                <Button
                  type="button"
                  colorScheme="blue"
                  flex="1"
                  onClick={onApply}
                  isDisabled={applyDisabled || applyLoading}
                  isLoading={applyLoading}
                >
                  {t(applyTitle)}
                </Button>
              ) : null}
            </Flex>
          </ModalFooter>
        ) : null}
      </ModalContent>
    </Modal>
  );
}
